import { useEffect, useState } from 'react';
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import Modal from './Modal/Modal';

/**
 * ConfirmDialog - A styled confirmation dialog that matches the site design
 * Replaces native browser confirm() dialogs with styled modal
 *
 * Optional `confirmTextMatch`: when set, the user must type that exact string
 * before the confirm button enables — used for destructive actions on
 * data-bearing records.
 */
const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "OK",
  cancelText = "Cancel",
  variant = "warning",
  confirmTextMatch = null,
  checkboxLabel = null,
  showIcon = true,
}) => {
  const [typed, setTyped] = useState('');
  const [checkboxChecked, setCheckboxChecked] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTyped('');
      setCheckboxChecked(false);
    }
  }, [isOpen]);

  const requiresTyping = !!confirmTextMatch;
  const matches = !requiresTyping || typed.trim() === confirmTextMatch;

  const handleConfirm = () => {
    if (!matches) return;
    if (checkboxLabel) onConfirm(checkboxChecked);
    else onConfirm();
    onClose();
  };

  // Variant styles for different dialog types
  const variantStyles = {
    danger: {
      Icon: ExclamationTriangleIcon,
      confirmBtn: "btn-error",
      titleColor: "text-error"
    },
    warning: {
      Icon: ExclamationTriangleIcon,
      confirmBtn: "btn-warning",
      titleColor: "text-warning"
    },
    info: {
      Icon: InformationCircleIcon,
      confirmBtn: "btn-primary",
      titleColor: "text-primary"
    }
  };

  const style = variantStyles[variant] || variantStyles.warning;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className={`flex items-center gap-2 ${showIcon ? style.titleColor : ''}`}>
          {showIcon && <style.Icon aria-hidden="true" className="h-6 w-6 shrink-0" />}
          {title}
        </span>
      }
      actions={
        <>
          <button
            className="btn btn-ghost"
            onClick={onClose}
          >
            {cancelText}
          </button>
          <button
            className={`btn ${style.confirmBtn}`}
            onClick={handleConfirm}
            disabled={!matches}
          >
            {confirmText}
          </button>
        </>
      }
    >
      <div className="text-base whitespace-pre-line">
        {message}
      </div>
      {checkboxLabel && (
        <label className="mt-4 flex cursor-pointer items-center gap-3 text-sm">
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={checkboxChecked}
            onChange={(event) => setCheckboxChecked(event.target.checked)}
          />
          <span>{checkboxLabel}</span>
        </label>
      )}
      {requiresTyping && (
        <div className="mt-4 space-y-2">
          <label className="text-sm text-base-content/70">
            Type <span className="font-semibold text-base-content">{confirmTextMatch}</span> to confirm:
          </label>
          <input
            type="text"
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && matches) handleConfirm(); }}
            className="input input-bordered w-full"
            placeholder={confirmTextMatch}
          />
        </div>
      )}
    </Modal>
  );
};

export default ConfirmDialog;
